import { useQuery } from "@tanstack/react-query";
import { personService } from "@/features/pessoas/services/personService";
import { rotuloCodigoNome } from "@/lib/consultas";
import { cn } from "@/lib/utils";

interface PessoaNomeProps {
  /** Id da pessoa (wrd_clientes): cliente, fornecedor, vendedor, funcionário... */
  id?: number | null;
  /** Prefixa o código ("123 — NOME"). Desligue quando a grade já tem coluna Código. */
  comCodigo?: boolean;
  /** Texto quando não há id. */
  vazio?: string;
  className?: string;
}

/**
 * Exibe o nome de uma pessoa a partir do id — fallback para DTOs que ainda só trazem
 * `clienteId`/`fornecedorId`. Nunca mostra "#id": se a pessoa não existir, diz isso.
 * Compartilha o cache ["pessoa", id] com o PessoaSearchCombobox.
 */
export function PessoaNome({ id, comCodigo = true, vazio = "—", className }: PessoaNomeProps) {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["pessoa", id],
    queryFn: () => personService.getById(id!),
    enabled: !!id,
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!id) return <span className={cn("text-muted-foreground", className)}>{vazio}</span>;
  if (isLoading) return <span className={cn("text-muted-foreground", className)}>…</span>;
  if (isError || !data) {
    return <span className={cn("text-muted-foreground italic", className)}>(pessoa não encontrada)</span>;
  }

  const texto = comCodigo ? rotuloCodigoNome(data.id, data.nomeRazao) : data.nomeRazao;
  return <span className={className} title={data.cpfCnpj ? `${texto} · ${data.cpfCnpj}` : texto}>{texto}</span>;
}
